
import type { MenuItemPermission } from 'src/types/menu-item-permission';

import { useMemo } from 'react';

import { useAuthContext } from 'src/auth/hooks';

/**
 * Hook to check user permissions
 * @returns Object with permission checking methods
 */
export function useUserPermissions() {
  const { user } = useAuthContext();

  const userPermissions = useMemo(() => {
    if (!user?.rolesPermissions) {
      return [];
    }
    return user.rolesPermissions;
  }, [user?.rolesPermissions]);

  const explicitPermissions = useMemo(() => {
    if (!user?.explicitPermissions) {
      return [];
    }
    return user.explicitPermissions;
  }, [user?.explicitPermissions]);

  const isOrgRoleUser = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes('custom'),
    [user?.roles]
  );

  /**
   * Check if user has any of the specified permissions
   * @param permissions - Array of permissions to check
   * @param level - Optional level filter
   * @returns boolean indicating if user has any of the permissions
   */
  const hasAnyPermission = useMemo(
    () =>
      (permissions?: MenuItemPermission[] | undefined): boolean => {
        if (!userPermissions.length && permissions && permissions.length > 0) {
          return false;
        }

        if(!permissions || permissions?.length === 0) {
          return true;
        }

        return userPermissions.some((userPermission) =>
          permissions.some(
            (permission) =>
              permission.permission === userPermission.permission &&
              (!permission.levels ||
                permission.levels.length === 0 ||
                permission.levels?.includes(userPermission.level))
          )
        );
      },
    [userPermissions]
  );

  const hasAnyExplicitPermission = useMemo(
    () =>
      (permissions?: MenuItemPermission[] | undefined): boolean => {
        const permsToCheck = isOrgRoleUser ? explicitPermissions : userPermissions;

        if (!permsToCheck.length && permissions && permissions.length > 0) {
          return false;
        }

        if(!permissions || permissions?.length === 0) {
          return true;
        }

        return permsToCheck.some((userPermission) =>
          permissions.some(
            (permission) =>
              permission.permission === userPermission.permission &&
              (!permission.levels ||
                permission.levels.length === 0 ||
                permission.levels?.includes(userPermission.level))
          )
        );
      },
    [explicitPermissions, userPermissions, isOrgRoleUser]
  );

  /**
   * Check if user has all of the specified permissions
   * @param permissions - Array of permissions to check
   * @param level - Optional level filter
   * @returns boolean indicating if user has all permissions
   */
  const hasAllPermissions = useMemo(
    () =>
      (permissions?: MenuItemPermission[] | undefined): boolean => {
        if (!userPermissions.length && permissions && permissions.length > 0) {
          return false;
        }

        if(!permissions || permissions?.length === 0) {
          return true;
        }

        return permissions.every((permission) =>
          userPermissions.some(
            (userPermission) =>
              userPermission.permission === permission.permission &&
              (!permission.levels ||
                permission.levels.length === 0 ||
                permission.levels?.includes(userPermission.level))
          )
        );
      },
    [userPermissions]
  );

  const hasPermissionAtExactLevel = useMemo(
    () =>
      (permissions?: MenuItemPermission[] | undefined): boolean => {
        const permsToCheck = isOrgRoleUser ? explicitPermissions : userPermissions;

        if (!permsToCheck.length && permissions && permissions.length > 0) {
          return false;
        }

        if (!permissions || permissions?.length === 0) {
          return true;
        }

        return permsToCheck.some((userPermission) =>
          permissions.some(
            (permission) =>
              permission.permission === userPermission.permission &&
              (!permission.levels ||
                permission.levels.length === 0 ||
                permission.levels?.includes(userPermission.level))
          )
        );
      },
    [explicitPermissions, userPermissions, isOrgRoleUser]
  );

  return {
    user,
    hasAnyPermission,
    hasAnyExplicitPermission,
    hasAllPermissions,
    hasPermissionAtExactLevel,
    userPermissions,
  };
}
